import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/brand/Logo";

const input = "w-full h-11 px-3 rounded-lg border border-input bg-white focus:outline-none focus:ring-2 focus:ring-electric text-sm";

function Shell({ title, children }) {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="w-full max-w-sm">
        <div className="flex justify-center mb-8"><Logo /></div>
        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <h1 className="font-heading text-xl font-bold text-navy">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState(""); const [loading, setLoading] = useState(false); const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); toast.success("If an account exists, a reset link was sent."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Reset your password">
      {sent ? <p className="mt-4 text-sm text-slate-600">Check your email (or the server console in dev) for a reset link.</p> : (
        <form onSubmit={submit} className="mt-4 space-y-4" data-testid="forgot-form">
          <input className={input} type="email" placeholder="Your email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="forgot-email" />
          <Button type="submit" disabled={loading} className="w-full bg-electric hover:bg-tech" data-testid="forgot-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}</Button>
        </form>
      )}
      <Link to="/owner/login" className="block text-center text-sm text-electric mt-4">Back to login</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [password, setPassword] = useState(""); const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/auth/reset-password", { token: params.get("token"), password }); toast.success("Password updated! Please log in."); nav("/owner/login"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <Shell title="Choose a new password">
      <form onSubmit={submit} className="mt-4 space-y-4" data-testid="reset-form">
        <input className={input} type="password" placeholder="New password (min 6)" required value={password} onChange={e => setPassword(e.target.value)} data-testid="reset-password" />
        <Button type="submit" disabled={loading} className="w-full bg-electric hover:bg-tech" data-testid="reset-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}</Button>
      </form>
    </Shell>
  );
}
